import { useEffect, useState } from "react";
import { Plus, Trash2, TicketPercent } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";
const EMPTY = { code: "", percent: 10, usage_limit: 0, active: true };

export default function PromoTab() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", form);
      toast.success(`Code ${form.code.toUpperCase()} créé`);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggle = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { code: p.code, percent: p.percent, usage_limit: p.usage_limit, active: !p.active });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Code supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6" data-testid="promo-admin">
      <form onSubmit={create} className="card-tactical grid gap-4 p-5" data-testid="admin-promo-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <TicketPercent className="h-5 w-5 text-orange-500" /> Créer un code promo
        </p>
        <div className="grid gap-4 sm:grid-cols-4">
          <input placeholder="DISCORD10" value={form.code} data-testid="admin-promo-code"
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} required />
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Remise (%)
            <input type="number" min="1" max="100" value={form.percent} data-testid="admin-promo-percent"
              onChange={(e) => setForm({ ...form, percent: +e.target.value || 0 })} className={inputCls + " mt-1"} required />
          </label>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Limite d'utilisations (0 = illimité)
            <input type="number" min="0" value={form.usage_limit} data-testid="admin-promo-limit"
              onChange={(e) => setForm({ ...form, usage_limit: +e.target.value || 0 })} className={inputCls + " mt-1"} />
          </label>
          <div className="flex items-end">
            <button type="submit" data-testid="admin-promo-submit"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
              <Plus className="h-4 w-4" /> Créer
            </button>
          </div>
        </div>
      </form>

      <div className="grid gap-3">
        {promos.length === 0 && <p className="text-sm text-slate-500">Aucun code promo. Créez-en un pour vos abonnés Discord !</p>}
        {promos.map((p) => (
          <div key={p.id} data-testid={`admin-promo-row-${p.code}`}
            className="card-tactical flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1 font-mono2 font-bold uppercase text-orange-400">{p.code}</span>
              <span className="text-sm font-bold text-white">-{p.percent} %</span>
              <span className="text-xs text-slate-400">
                {p.usage_limit > 0 ? `${p.used_count}/${p.usage_limit} utilisations` : `${p.used_count} utilisation(s)`}
              </span>
              <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700 text-slate-300"}`}>
                {p.active ? "Actif" : "Désactivé"}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(p)} data-testid={`admin-promo-toggle-${p.code}`}
                className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase transition-all ${p.active ? "border-slate-600 text-slate-300 hover:border-orange-500" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"}`}>
                {p.active ? "Désactiver" : "Activer"}
              </button>
              <button onClick={() => remove(p)} data-testid={`admin-promo-delete-${p.code}`} aria-label={`Supprimer ${p.code}`}
                className="rounded-lg border border-red-500/40 p-1.5 text-red-400 transition-all hover:bg-red-500/10">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
